import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getShorts, deleteShort, updateShortStatus } from "../../utils/backend";
import {
  CSpinner,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CRow,
  CCol,
  CFormLabel,
  CButton,
  CCardFooter,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CFormSelect,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionButton,
  CAccordionBody,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CInputGroup,
} from "@coreui/react-pro";

import { CPagination, CPaginationItem } from "@coreui/react-pro";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CIcon from "@coreui/icons-react";
import {
  cilSortAlphaUp,
  cilSortAlphaDown,
  cilSortNumericUp,
  cilSortNumericDown,
  cilSwapVertical,
} from "@coreui/icons";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";
import { setUppercase } from "../../utils/SetUppercase";

const Shorts = () => {
  const hasPermission = (permissionGroup, permissionName) => {
    let permissions;
    try {
      permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      permissions = [];
    }

    return permissions.some(
      (perm) =>
        perm.permission_group === permissionGroup &&
        perm.permission_name === permissionName
    );
  };

  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [admin_name, setAdminName] = useState("");
  const [description, setDescription] = useState("");
  const [fileName, setFileName] = useState("");
  const [status, setStatus] = useState("");
  const [start_time, setStartTime] = useState("");
  const [end_time, setEndTime] = useState("");
  const [updated_start_time, setUpdatedStartTime] = useState("");
  const [updated_end_time, setUpdatedEndTime] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [shortToDelete, setShortToDelete] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });

  useEffect(() => {
    // Load filters and pagination state from localStorage on page load
    const savedState = JSON.parse(localStorage.getItem("shortsState"));
    if (savedState) {
      setTitle(savedState.title || "");
      setStatus(savedState.status || "");
      setAdminName(savedState.username || "");
      setDescription(savedState.description || "");
      setFileName(savedState.filename || "");
      setStartTime(savedState.start_date || "");
      setEndTime(savedState.end_date || "");
      setUpdatedStartTime(savedState.updated_start_date || "");
      setUpdatedEndTime(savedState.updated_end_date || "");
      setPerPage(savedState.perPage || 10);

      // Fetch data based on the saved state
      // Always land on page 1. Restoring a saved page number stranded admins on
      // stale rows: the list is sorted newest-first, so page N>1 never shows new
      // rows and nothing on screen explained why.
      const filters = {
        title: savedState.title || "",
        status: savedState.status || "",
        username: savedState.username,
        description: savedState.description,
        filename: savedState.filename,
        start_date: savedState.start_date,
        end_date: savedState.end_date,
        updated_start_date: savedState.updated_start_date,
        updated_end_date: savedState.updated_end_date,
        page: 1,
        perPage: savedState.perPage || 10,
      };
      fetchData(filters);
    } else {
      fetchData();
    }
  }, []);

  const fetchData = async (filters = { perPage: perPage }) => {
    //setIsLoading(true);
    try {
      const response = await getShorts(filters);
      setAuditLogs(setUppercase(response.data.data));
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAuditLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveStateToLocalStorage = () => {
    const existingState = JSON.parse(localStorage.getItem("shortsState")) || {};
    // Evict keys written by older builds; the merge below would otherwise keep a
    // stale currentPage alive in every admin's browser indefinitely.
    delete existingState.currentPage;
    const state = {
      username: admin_name,
      title,
      description,
      filename: fileName,
      status,
      start_date: start_time,
      end_date: end_time,
      updated_start_date: updated_start_time,
      updated_end_date: updated_end_time,
    };
    localStorage.setItem(
      "shortsState",
      JSON.stringify({ ...existingState, ...state })
    );
  };

  const handleSearch = async (e) => {
    const filters = {
      username: admin_name,
      title,
      description,
      filename: fileName,
      status,
      start_date: start_time,
      end_date: end_time,
      updated_start_date: updated_start_time,
      updated_end_date: updated_end_time,
      page: 1,
      perPage: perPage,
    };
    saveStateToLocalStorage(); // Save state before fetching data
    await fetchData(filters);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    admin_name,
    title,
    description,
    fileName,
    status,
    start_time,
    end_time,
    updated_start_time,
    updated_end_time,
    perPage,
  ]);

  const handleReset = () => {
    setAdminName("");
    setTitle("");
    setDescription("");
    setFileName("");
    setStatus("");
    setStartTime("");
    setEndTime("");
    setUpdatedStartTime("");
    setUpdatedEndTime("");
    setCurrentPage(1);

    // Retain only perPage in localStorage
    const existingState = JSON.parse(localStorage.getItem("shortsState")) || {};
    localStorage.setItem(
      "shortsState",
      JSON.stringify({ perPage: existingState.perPage || 10 })
    );

    fetchData();
  };

  const handlePageChange = async (page) => {
    const filters = {
      username: admin_name,
      title,
      description,
      filename: fileName,
      status,
      start_date: start_time,
      end_date: end_time,
      updated_start_date: updated_start_time,
      updated_end_date: updated_end_time,
      page: page,
      perPage: perPage,
    };
    await fetchData(filters);
  };

  const handlePerPageChange = async (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to the first page
    localStorage.setItem(
      "shortsState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("shortsState")),
        perPage: newPerPage,
      })
    );
    const filters = {
      username: admin_name,
      title,
      description,
      filename: fileName,
      status,
      start_date: start_time,
      end_date: end_time,
      updated_start_date: updated_start_time,
      updated_end_date: updated_end_time,
      page: 1,
      perPage: newPerPage,
    };
    await fetchData(filters);
  };

  const handleDelete = async (shortToDelete) => {
    try {
      await deleteShort({ id: shortToDelete });
      toast.success("Short deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.message);
      console.error("Failed to delete short:", error);
    } finally {
      setDeleteModalVisible(false);
      setShortToDelete(null);
    }
  };

  const confirmDelete = (id) => {
    setShortToDelete(id);
    setDeleteModalVisible(true);
  };

  const handleStatusChange = async (userId, newStatus) => {
    if (!hasPermission("Short Management", "ChangeStatus")) {
      toast.error("You do not have permission to change status");
      return;
    }
    const updatedStatus =
      newStatus.charAt(0).toUpperCase() + newStatus.slice(1);

    // Find the original short and store its status
    const originalUser = auditLogs.find((short) => short.id === userId);
    const originalStatus = originalUser ? originalUser.status : null;
    // If the new status is the same as the current status, do nothing
    if (originalStatus === updatedStatus) {
      return;
    }

    // Optimistically update the UI
    setAuditLogs((prevLogs) =>
      prevLogs.map((short) =>
        short.id === userId ? { ...short, status: updatedStatus } : short
      )
    );

    try {
      const response = await updateShortStatus({
        id: userId,
        status: newStatus,
      });
      if (response.success) {
        toast.success("Short status updated successfully");
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");

      // Revert the status if the update fails
      setAuditLogs((prevLogs) =>
        prevLogs.map((short) =>
          short.id === userId ? { ...short, status: originalStatus } : short
        )
      );
    }
  };

  const generatePerPageOptions = (totalItems) => {
    if (totalItems <= 10) {
      return [10];
    }
    const options = [];
    let interval = 10;
    while (interval < totalItems) {
      options.push(interval);
      if (interval >= 50) {
        interval += 50;
      } else {
        interval *= 5;
      }
    }
    const nearestHigherMultipleOf50 = Math.ceil(totalItems / 50) * 50;
    options.push(nearestHigherMultipleOf50);
    return options;
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc"; // Toggle back to ascending
    }
    setSortConfig({ key, direction });
    setAuditLogs((prevLogs) => [...prevLogs]); // Trigger re-render to apply styles
  };

  const sortedAuditLogs = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return auditLogs;
    return [...auditLogs].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "status") {
        aValue = a.status || "";
        bValue = b.status || "";
      }

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [auditLogs, sortConfig]);

  // Surfaced next to Reset so an admin can never be silently filtered down to a subset.
  const activeFilterCount = [
    admin_name,
    title,
    description,
    fileName,
    status,
    start_time,
    end_time,
    updated_start_time,
    updated_end_time,
  ].filter(function (v) {
    return v !== "" && v !== null && v !== undefined;
  }).length;

  return (
    <>
      <CCard className="border-light">
        <CCardBody style={{ overflowX: "auto" }}>
          {isLoading ? (
            <div>
              <Skeleton height={40} count={1} />
              <Skeleton height={30} count={5} style={{ marginTop: "10px" }} />
            </div>
          ) : (
            <>
              <CRow>
                <CCol className="justify-content-end d-flex my-1 gap-3 align-items-center">
                  {activeFilterCount > 0 && (
                    <span
                      className="me-3 text-nowrap"
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffe69c",
                        borderRadius: "12px",
                        color: "#664d03",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        padding: "4px 10px",
                      }}
                    >
                      <i className="bi bi-funnel-fill me-1"></i>
                      {activeFilterCount}{" "}
                      {activeFilterCount === 1 ? "filter" : "filters"} active —
                      showing a subset
                    </span>
                  )}
                  {hasPermission("Short Management", "Create") && (
                    <CButton
                      className="ansa-button-primary"
                      onClick={() => navigate("/shorts/create")}
                    >
                      Create New Short
                    </CButton>
                  )}
                  <CButton
                    className="ansa-button-secondary me-3"
                    type="button"
                    onClick={handleReset}
                  >
                    Reset
                  </CButton>
                </CCol>
              </CRow>
              <div style={{ overflowX: "auto" }}>
                <CTable striped borderless>
                  <CTableHead>
                    <CTableRow>
                      <CTableDataCell></CTableDataCell>
                      <CTableDataCell></CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={admin_name}
                          onChange={(e) => setAdminName(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={fileName}
                          onChange={(e) => setFileName(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormSelect
                          id="status"
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          <option value="">Select Status</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                          <option value="pending review">Pending Review</option>
                        </CFormSelect>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CInputGroup style={{ maxWidth: "250px" }}>
                          <CFormInput
                            type="date"
                            value={start_time}
                            onChange={(e) => setStartTime(e.target.value)}
                            title="Start Date"
                          />

                          <span className="input-group-text bg-light text-muted small">
                            to
                          </span>

                          <CFormInput
                            type="date"
                            value={end_time}
                            onChange={(e) => setEndTime(e.target.value)}
                            title="End Date"
                          />
                        </CInputGroup>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CInputGroup style={{ maxWidth: "250px" }}>
                          <CFormInput
                            type="date"
                            value={updated_start_time}
                            onChange={(e) =>
                              setUpdatedStartTime(e.target.value)
                            }
                            title="Start Date"
                          />

                          <span className="input-group-text bg-light text-muted small">
                            to
                          </span>

                          <CFormInput
                            type="date"
                            value={updated_end_time}
                            onChange={(e) => setUpdatedEndTime(e.target.value)}
                            title="End Date"
                          />
                        </CInputGroup>
                      </CTableDataCell>
                    </CTableRow>
                    <CTableRow>
                      <CTableHeaderCell
                        onClick={() => {}}
                        className={`hover-header ${
                          sortConfig.key === "id" ? "ctable-active-header" : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "80px",
                        }}
                      >
                        No.{" "}
                        {sortConfig.key === "id" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortNumericDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortNumericUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell>Actions</CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("user_name")}
                        className={`hover-header ${
                          sortConfig.key === "user_name"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "150px",
                        }}
                      >
                        Username{" "}
                        {sortConfig.key === "user_name" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortAlphaDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortAlphaUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("title")}
                        className={`hover-header ${
                          sortConfig.key === "title"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "150px",
                        }}
                      >
                        Title{" "}
                        {sortConfig.key === "title" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortAlphaDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortAlphaUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("description")}
                        className={`hover-header ${
                          sortConfig.key === "description"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "200px",
                        }}
                      >
                        Description{" "}
                        {sortConfig.key === "description" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortAlphaDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortAlphaUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("file_url")}
                        className={`hover-header ${
                          sortConfig.key === "file_url"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "150px",
                        }}
                      >
                        File{" "}
                        {sortConfig.key === "file_url" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortAlphaDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortAlphaUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("status")}
                        className={`hover-header ${
                          sortConfig.key === "status"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "120px",
                        }}
                      >
                        Status{" "}
                        {sortConfig.key === "status" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortAlphaDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortAlphaUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("created_at")}
                        className={`hover-header ${
                          sortConfig.key === "created_at"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "250px",
                        }}
                      >
                        Created At{" "}
                        {sortConfig.key === "created_at" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortNumericDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortNumericUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("updated_at")}
                        className={`hover-header ${
                          sortConfig.key === "updated_at"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                          minWidth: "250px",
                        }}
                      >
                        Updated At{" "}
                        {sortConfig.key === "updated_at" ? (
                          sortConfig.direction === "asc" ? (
                            <CIcon
                              icon={cilSortNumericDown}
                              size="lg"
                              className="float-end"
                            />
                          ) : (
                            <CIcon
                              icon={cilSortNumericUp}
                              size="lg"
                              className="float-end"
                            />
                          )
                        ) : (
                          <CIcon
                            icon={cilSwapVertical}
                            size="lg"
                            className="float-end text-muted"
                            style={{ opacity: 0.5 }}
                          />
                        )}
                      </CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {sortedAuditLogs.map((short, index) => (
                      <CTableRow key={short.id}>
                        <CTableDataCell>
                          {index + 1 + perPage * (currentPage - 1)}
                        </CTableDataCell>
                        <CTableDataCell>
                          <div className="d-flex align-items-center gap-2">
                            {hasPermission("Short Management", "View") && (
                              <CButton
                                className="btn btn-sm btn-info text-white"
                                title="Details"
                                onClick={() =>
                                  navigate(`/shorts/details/${short.id}`)
                                }
                              >
                                <i className="bi bi-eye-fill"></i>
                              </CButton>
                            )}
                            {hasPermission("Short Management", "Edit") && (
                              <CButton
                                className="btn btn-sm btn-warning text-white"
                                title="Edit"
                                onClick={() =>
                                  navigate(`/shorts/edit/${short.id}`)
                                }
                              >
                                <i className="bi bi-pencil-fill"></i>
                              </CButton>
                            )}
                            {hasPermission("Short Management", "Delete") && (
                              <CButton
                                className="btn btn-sm btn-danger text-white"
                                title="Delete"
                                onClick={() => confirmDelete(short.id)}
                              >
                                <i className="bi bi-trash-fill"></i>
                              </CButton>
                            )}
                          </div>
                        </CTableDataCell>
                        <CTableDataCell>
                          {short.user_name || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>{short.title || "N/A"}</CTableDataCell>
                        <CTableDataCell>
                          {short.description || "N/A"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {short.file_url ? (
                            <a
                              href={
                                short.file_url.includes("/uploads/")
                                  ? short.file_url
                                  : short.file_url.replace(
                                      /^(https?:\/\/[^\/]+\/)/,
                                      "$1uploads/"
                                    )
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {short.file_url.split("/").pop()}
                            </a>
                          ) : (
                            "N/A"
                          )}
                        </CTableDataCell>{" "}
                        <CTableDataCell>
                          <CDropdown>
                            <CDropdownToggle
                              color="secondary"
                              variant="outline"
                              disabled={
                                !hasPermission("Short Management", "Edit")
                              }
                              style={{
                                backgroundColor:
                                  short.status === "Approved"
                                    ? "#66BB6A"
                                    : short.status === "Rejected"
                                    ? "#8B0000"
                                    : "#A9A9A9", // Assuming Pending Review is orange
                                color: "#fff",
                                border: "none",
                                width: "150px",
                                borderRadius: "20px",
                                fontWeight: "500",
                                fontSize: "14px",
                              }}
                            >
                              {short.status
                                ? short.status.toUpperCase()
                                : "N/A"}
                            </CDropdownToggle>
                            <CDropdownMenu>
                              <CDropdownItem
                                onClick={() =>
                                  handleStatusChange(short.id, "Approved")
                                }
                              >
                                Approved
                              </CDropdownItem>
                              <CDropdownItem
                                onClick={() =>
                                  handleStatusChange(short.id, "Rejected")
                                }
                              >
                                Rejected
                              </CDropdownItem>
                              <CDropdownItem
                                onClick={() =>
                                  handleStatusChange(short.id, "Pending Review")
                                }
                              >
                                Pending Review
                              </CDropdownItem>
                            </CDropdownMenu>
                          </CDropdown>
                        </CTableDataCell>
                        <CTableDataCell style={{ minWidth: "110px" }}>
                          {short.created_at
                            ? formatDateTime(short.created_at)
                            : "N/A"}
                        </CTableDataCell>
                        <CTableDataCell style={{ minWidth: "110px" }}>
                          {short.updated_at
                            ? formatDateTime(short.updated_at)
                            : "N/A"}
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              </div>
            </>
          )}

          {!isLoading && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div>
                <CPagination
                  className="mx-2"
                  aria-label="Page navigation example"
                  onActivePageChange={async (page) => {
                    setCurrentPage(page);
                    await fetchData(
                      {
                        title,
                        admin_name,
                        status,
                        start_time,
                        end_time,
                        perPage: perPage,
                      },
                      page
                    );
                  }}
                  activePage={currentPage}
                  pages={totalPages}
                  doubleArrows={false}
                  arrows
                >
                  <CPaginationItem
                    aria-label="Previous"
                    disabled={currentPage <= 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </CPaginationItem>
                  {[...Array(totalPages).keys()].map((pageNumber) => {
                    if (
                      pageNumber === 0 || // First page
                      pageNumber === totalPages - 1 || // Last page
                      pageNumber < 5 || // First few pages
                      (pageNumber >= currentPage - 2 &&
                        pageNumber <= currentPage + 2) // Pages around the current page
                    ) {
                      return (
                        <CPaginationItem
                          key={pageNumber + 1}
                          active={currentPage === pageNumber + 1}
                          onClick={() => handlePageChange(pageNumber + 1)}
                        >
                          {pageNumber + 1}
                        </CPaginationItem>
                      );
                    } else if (
                      (pageNumber === 5 && currentPage > 7) || // Ellipsis after the first few pages
                      (pageNumber === currentPage + 3 &&
                        currentPage < totalPages - 4) // Ellipsis before the last page
                    ) {
                      return (
                        <CPaginationItem
                          key={`ellipsis-${pageNumber}`}
                          disabled
                        >
                          ...
                        </CPaginationItem>
                      );
                    } else {
                      return null;
                    }
                  })}
                  <CPaginationItem
                    aria-label="Next"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </CPaginationItem>
                </CPagination>
              </div>
              <div className="d-flex align-items-center">
                <span className="me-3">
                  Showing {auditLogs.length} of {totalItems} shorts
                </span>
                <CFormSelect
                  className="w-auto"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                >
                  {generatePerPageOptions(totalItems).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
      <CModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        alignment="center"
      >
        <CModalHeader>
          <CModalTitle>Confirm Delete</CModalTitle>
        </CModalHeader>
        <CModalBody>Are you sure you want to delete this short?</CModalBody>
        <CModalFooter>
          <CButton
            className="ansa-button-secondary"
            onClick={() => setDeleteModalVisible(false)}
          >
            Cancel
          </CButton>
          <CButton
            className="ansa-button-primary"
            onClick={() => handleDelete(shortToDelete)}
          >
            Delete
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default Shorts;
